import {
  type UseMutationResult,
  type UseQueryResult,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import type { SupabaseClientLike } from './people';

type SupabaseQueryResult<T = unknown> = { data: T | null; error: { message: string } | null };
type QueryBuilderLike = PromiseLike<SupabaseQueryResult<unknown>> & {
  select: (...args: unknown[]) => QueryBuilderLike;
  eq: (...args: unknown[]) => QueryBuilderLike;
  order: (...args: unknown[]) => QueryBuilderLike;
  insert: (...args: unknown[]) => QueryBuilderLike;
  update: (...args: unknown[]) => QueryBuilderLike;
  delete: () => QueryBuilderLike;
  upsert: (...args: unknown[]) => QueryBuilderLike;
  single: () => Promise<SupabaseQueryResult<unknown>>;
};
type SupabaseTableClient = { from: (table: string) => QueryBuilderLike };

const tc = (supabase: SupabaseClientLike): SupabaseTableClient =>
  supabase as unknown as SupabaseTableClient;

// =============================================================================
// Types
// =============================================================================

export interface RuleTree {
  id: string;
  tenant_id: string;
  name: string;
  scope: string;
  description: string | null;
  is_active: boolean;
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

export interface RuleNode {
  id: string;
  tree_id: string;
  tenant_id: string;
  parent_id: string | null;
  kind: string;
  config: Record<string, unknown>;
  order_index: number;
  created_at: string;
}

// =============================================================================
// Query Keys
// =============================================================================

export const ruleKeys = {
  trees: (tenantId: string) => ['rule_trees', tenantId] as const,
  nodes: (tenantId: string, treeId: string) => ['rule_nodes', tenantId, treeId] as const,
} as const;

// =============================================================================
// Hooks — rule_trees
// =============================================================================

export function useRuleTrees(
  supabase: SupabaseClientLike,
  tenantId: string,
): UseQueryResult<RuleTree[]> {
  return useQuery({
    queryKey: ruleKeys.trees(tenantId),
    queryFn: async () => {
      const { data, error } = await tc(supabase)
        .from('rule_trees')
        .select('*')
        .eq('tenant_id', tenantId)
        .order('created_at', { ascending: false });
      if (error) throw error;
      return (data ?? []) as RuleTree[];
    },
    enabled: Boolean(tenantId),
  });
}

export function useCreateRuleTree(
  supabase: SupabaseClientLike,
  tenantId: string,
): UseMutationResult<RuleTree, Error, { name: string; scope: string; description?: string }> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input) => {
      const { data, error } = await tc(supabase)
        .from('rule_trees')
        .insert({
          tenant_id: tenantId,
          name: input.name,
          scope: input.scope,
          description: input.description ?? null,
          is_active: true,
        })
        .select()
        .single();
      if (error) throw error;
      return data as RuleTree;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ruleKeys.trees(tenantId) }),
  });
}

export function useUpdateRuleTree(
  supabase: SupabaseClientLike,
  tenantId: string,
): UseMutationResult<
  void,
  Error,
  { id: string } & Partial<Pick<RuleTree, 'name' | 'description' | 'is_active'>>
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...patch }) => {
      const { error } = await tc(supabase)
        .from('rule_trees')
        .update(patch)
        .eq('id', id)
        .eq('tenant_id', tenantId);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ruleKeys.trees(tenantId) }),
  });
}

export function useDeleteRuleTree(
  supabase: SupabaseClientLike,
  tenantId: string,
): UseMutationResult<void, Error, { id: string }> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id }) => {
      const { error } = await tc(supabase)
        .from('rule_trees')
        .delete()
        .eq('id', id)
        .eq('tenant_id', tenantId);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ruleKeys.trees(tenantId) }),
  });
}

// =============================================================================
// Hooks — rule_nodes
// =============================================================================

export function useRuleNodes(
  supabase: SupabaseClientLike,
  tenantId: string,
  treeId: string,
): UseQueryResult<RuleNode[]> {
  return useQuery({
    queryKey: ruleKeys.nodes(tenantId, treeId),
    queryFn: async () => {
      const { data, error } = await tc(supabase)
        .from('rule_nodes')
        .select('*')
        .eq('tree_id', treeId)
        .eq('tenant_id', tenantId)
        .order('order_index', { ascending: true });
      if (error) throw error;
      return (data ?? []) as RuleNode[];
    },
    enabled: Boolean(tenantId) && Boolean(treeId),
  });
}

export function useUpsertRuleNode(
  supabase: SupabaseClientLike,
  tenantId: string,
): UseMutationResult<RuleNode, Error, Omit<RuleNode, 'id' | 'created_at'> & { id?: string }> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input) => {
      const payload: Record<string, unknown> = {
        tree_id: input.tree_id,
        tenant_id: tenantId,
        parent_id: input.parent_id ?? null,
        kind: input.kind,
        config: input.config,
        order_index: input.order_index,
      };
      if (input.id) payload.id = input.id;
      const { data, error } = await tc(supabase)
        .from('rule_nodes')
        .upsert(payload, { onConflict: 'id' })
        .select()
        .single();
      if (error) throw error;
      return data as RuleNode;
    },
    onSuccess: (node) => qc.invalidateQueries({ queryKey: ruleKeys.nodes(tenantId, node.tree_id) }),
  });
}

export function useDeleteRuleNode(
  supabase: SupabaseClientLike,
  tenantId: string,
): UseMutationResult<void, Error, { id: string; tree_id: string }> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id }) => {
      const { error } = await tc(supabase)
        .from('rule_nodes')
        .delete()
        .eq('id', id)
        .eq('tenant_id', tenantId);
      if (error) throw error;
    },
    onSuccess: (_, variables) =>
      qc.invalidateQueries({ queryKey: ruleKeys.nodes(tenantId, variables.tree_id) }),
  });
}
